"use client";
import React from "react";
import { cva } from "class-variance-authority";
import { Icon } from "@/components/ui/Icon";
import type { ImageProps, VideoProps } from "@/types";
import type { HeadingStyle } from "@/components/ui/Heading";
import { Image } from "@/components/ui/Image";
import { Wysiwyg } from "../ui/Wysiwyg";
import { useBreakpoint, useCurrentBreakpoint } from "@/lib/hooks/useBreakpoint";

const quoteBlockVariants = cva("container max-md:mb-6", {
  variants: {
    variant: {
      light: "text-white childLinkLight",
      dark: "text-black childLinkDark",
    },
    inSlider: {
      false: "",
      true: "px-0!",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const mediaContainerVariants = cva("flex flex-col justify-center", {
  variants: {
    mediaWidth: {
      none: "hidden",
      half: "col-span-12 sm:col-span-7 sm:col-start-3 md:col-start-8 md:col-span-4 lg:col-start-8 lg:col-span-5",
      third: "col-span-4",
      twoThirds: "col-span-8",
    },
  },
  defaultVariants: {
    mediaWidth: "half",
  },
});

const textContainerVariants = cva("flex flex-col justify-center", {
  variants: {
    mediaWidth: {
      none: "col-span-12 sm:col-span-10 sm:col-start-3 md:col-start-3 md:col-span-7 lg:col-start-3 lg:col-span-8",
      half: "col-span-12 sm:col-span-10 sm:col-start-3 md:col-start-3 md:col-span-5 xl:col-start-2 xl:col-span-5",
      third: "col-span-8",
      twoThirds: "col-span-4",
    },
  },
  defaultVariants: {
    mediaWidth: "half",
  },
});

const iconVariants = cva(
  "static mb-sm sm:mb-0 sm:absolute sm:top-0 sm:-left-md sm:-translate-x-full",
  {
    variants: {
      variant: {
        light: "text-yellow",
        dark: "text-purple",
      },
    },
    defaultVariants: {
      variant: "light",
    },
  }
);

const headlineVariants = cva("", {
  variants: {
    headingStyle: {
      h1: "headline-1",
      h2: "md:text-4xl text-2xl",
      h3: "md:text-3xl text-2xl",
      h4: "headline-4",
      h5: "headline-5",
    },
  },
  defaultVariants: {
    headingStyle: "h3",
  },
});

const authorWrapperVariants = cva("", {
  variants: {
    quoteSize: {
      regular: "mt-lg",
      small: "mt-sm",
    },
  },
  defaultVariants: {
    quoteSize: "regular",
  },
});

const authorNameVariants = cva("not-italic", {
  variants: {},
  defaultVariants: {},
});

const authorRoleVariants = cva("uppercase text-sm", {
  variants: {
    variant: {
      light: "text-off-white",
      dark: "text-gray-dark",
    },
    quoteSize: {
      regular: "text-current mt-xs",
      small: "mt-0",
    },
  },
  defaultVariants: {
    variant: "light",
    quoteSize: "regular",
  },
});

export type MediaType = "image" | "video";

export interface MediaConfig {
  type: MediaType;
  image?: ImageProps;
  video?: VideoProps;
}

export interface QuoteBlockContentProps {
  variant?: "light" | "dark";
  media?: MediaConfig;
  text: string;
  authorName?: string;
  authorRole?: string;
  className?: string;
  inSlider?: boolean;
}

/**
 * QuoteBlock component - a quote with an optional author
 */
export const QuoteBlock: React.FC<QuoteBlockContentProps> = ({
  text,
  media,
  authorName,
  authorRole,
  variant = "light",
  className = "",
  inSlider = false,
}) => {
  const isWideScreen = useBreakpoint("sm");
  const isWithImage = !!media;
  const quoteSize = isWithImage ? "small" : "regular";
  const mediaWidth = isWithImage ? "half" : "none";
  const headingStyle: HeadingStyle = isWithImage ? "h3" : "h2";

  return (
    <div className={quoteBlockVariants({ variant, className, inSlider })}>
      <div className={textContainerVariants({ mediaWidth })}>
        <blockquote className="relative">
          <Icon
            name="quotation-mark"
            size={isWideScreen ? 64 : 40}
            className={iconVariants({ variant })}
            decorative
          />
          <Wysiwyg
            className={headlineVariants({ headingStyle })}
            html={text}
            mode="body"
          />
        </blockquote>
        <p className={authorWrapperVariants({ quoteSize })}>
          <cite className={authorNameVariants()}>{authorName}</cite>
          {authorRole && (
            <>
              <br />
              <span className={authorRoleVariants({ variant, quoteSize })}>
                {authorRole}
              </span>
            </>
          )}
        </p>
      </div>
      {media && media?.type === "image" && media?.image && (
        <div className={mediaContainerVariants({ mediaWidth })}>
          <div className={"relative w-full"}>
            <Image
              src={media.image.src}
              alt={media.image.alt}
              width={media.image.width}
              height={media.image.height}
              sizes={media.image.sizes}
              priority={media.image.priority}
              rounded="lg"
              className="h-full w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};
